import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

// Inactivity auto-logout. Sits on top of the 7-day absolute JWT (the hard backstop). A logged-in
// session is signed out after 8 HOURS of inactivity — same for every role. Per product decision this
// is a SILENT sign-out: no "Still there?" prompt, no countdown, no user action required. The
// last-activity timestamp is shared across tabs via localStorage so the idle window is honored
// everywhere, and activity in any tab keeps the whole session alive.
const IDLE_LIMIT_MS = 8 * 60 * 60 * 1000; // 8 hours, all roles
const LS_KEY = "os_last_activity";
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];

function readLastActivity() {
  const v = parseInt(localStorage.getItem(LS_KEY) || "", 10);
  return Number.isFinite(v) ? v : 0;
}

export default function IdleTimeout() {
  const { user, logout } = useAuth();
  const loggedIn = !!user && typeof user === "object";
  const lastWriteRef = useRef(0);

  const markActive = useCallback(() => {
    const now = Date.now();
    if (now - lastWriteRef.current < 1000) return; // throttle writes to once/second
    lastWriteRef.current = now;
    try { localStorage.setItem(LS_KEY, String(now)); } catch {}
  }, []);

  useEffect(() => {
    if (!loggedIn) return;
    // Seed a fresh window at session start — a stale timestamp from a previous session must never
    // make a brand-new login instantly idle.
    try { localStorage.setItem(LS_KEY, String(Date.now())); } catch {}
    lastWriteRef.current = Date.now();

    const onActivity = () => markActive();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const tick = () => {
      if (Date.now() - readLastActivity() >= IDLE_LIMIT_MS) logout();
    };
    tick(); // evaluate immediately on mount (catches a returning idle tab)
    const id = setInterval(tick, 30000);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [loggedIn, logout, markActive]);

  return null;
}
